import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { staffApi } from '../api/staff';
import { refShareApi } from '../api/services';
import { useAuth } from './AuthContext';

interface SidebarCounts {
  creatorsCount: number;
  partnersCount: number;
  accountsCount: number;
  cplOffersCount: number;
  cplOrdersCount: number;
  revshareOffersCount: number;
  revshareCampaignsCount: number;
  revshareBillsCount: number;
  withdrawRequestsCount: number;
}

interface SidebarCountsContextType {
  counts: SidebarCounts;
  isLoading: boolean;
  refreshCounts: () => Promise<void>;
}

const SidebarCountsContext = createContext<SidebarCountsContextType | undefined>(undefined);

export const useSidebarCounts = () => {
  const context = useContext(SidebarCountsContext);
  if (!context) {
    throw new Error('useSidebarCounts must be used within SidebarCountsProvider');
  }
  return context;
};

interface SidebarCountsProviderProps {
  children: ReactNode;
}

export const SidebarCountsProvider: React.FC<SidebarCountsProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [counts, setCounts] = useState<SidebarCounts>({
    creatorsCount: 0,
    partnersCount: 0,
    accountsCount: 0,
    cplOffersCount: 0,
    cplOrdersCount: 0,
    revshareOffersCount: 0,
    revshareCampaignsCount: 0,
    revshareBillsCount: 0,
    withdrawRequestsCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchCounts = async () => {
    try {
      setIsLoading(true);
      // Fetch all counts in parallel
      const [creators, partners, accounts, cplOffers, cplOrders, withdrawRequests, revshareOffers, revshareCampaigns, revshareBills] = await Promise.all([
        staffApi.getCreators({ page: 1, page_size: 1 }),
        staffApi.getPartners({ page: 1, page_size: 1 }),
        staffApi.getOnlyFansAccounts({ page: 1, page_size: 1 }),
        staffApi.getCPLOffers({ page: 1, page_size: 1 }),
        staffApi.getCPLOrders({ page: 1, page_size: 1 }),
        staffApi.getWithdrawalRequests({ page: 1, page_size: 1 }),
        refShareApi.getOffers({ page: 1, page_size: 1 }),
        refShareApi.getOrders({ page: 1, page_size: 1 }),
        refShareApi.getCosts({ page: 1, page_size: 1 }),
      ]);

      setCounts({
        creatorsCount: creators.count,
        partnersCount: partners.count,
        accountsCount: accounts.count,
        cplOffersCount: cplOffers.count,
        cplOrdersCount: cplOrders.count,
        revshareOffersCount: revshareOffers.count,
        revshareCampaignsCount: revshareCampaigns.count,
        revshareBillsCount: revshareBills.count,
        withdrawRequestsCount: withdrawRequests.count,
      });
    } catch (error) {
      console.error('Error fetching sidebar counts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch counts when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCounts();
    } else {
      // Reset counts when user logs out
      setCounts({
        creatorsCount: 0,
        partnersCount: 0,
        accountsCount: 0,
        cplOffersCount: 0,
        cplOrdersCount: 0,
        revshareOffersCount: 0,
        revshareCampaignsCount: 0,
        revshareBillsCount: 0,
        withdrawRequestsCount: 0,
      });
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const value: SidebarCountsContextType = {
    counts,
    isLoading,
    refreshCounts: fetchCounts,
  };

  return (
    <SidebarCountsContext.Provider value={value}>
      {children}
    </SidebarCountsContext.Provider>
  );
};
